import { motion } from "framer-motion";
import { ArrowRight, Car, Snowflake, Wifi, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { scrollToId } from "@/lib/scroll";
import api from "@/lib/api";
import AmbientGlow from "@/components/AmbientGlow";

const EASE = [0.22, 1, 0.36, 1];

const FALLBACK_PLANS = [
    {
        id: "individual-seat",
        name: "Individual Seat",
        price: "₹5,000",
        unit: "/ Seat / Month",
        suitable: "Freelancers, remote employees and individual professionals.",
        featured: true,
    },
    {
        id: "private-cabin",
        name: "Private Cabin",
        price: "₹5,500",
        unit: "/ Seat / Month",
        suitable: "Professionals, consultants and small teams.",
        featured: true,
    },
    {
        id: "premium-cabin",
        name: "Premium Cabin",
        price: "₹6,000",
        unit: "/ Seat / Month",
        suitable: "Businesses and professionals looking for a premium private workspace.",
        featured: true,
    },
];

const INCLUDES = [
    { icon: Wifi, label: "High-Speed Internet" },
    { icon: Snowflake, label: "Air Conditioning" },
    { icon: Zap, label: "Power Backup" },
    { icon: Car, label: "Free Parking" },
];

export default function PricingSection() {
    const [plans, setPlans] = useState(FALLBACK_PLANS);

    useEffect(() => {
        api.get("/api/plans")
            .then((res) => {
                if (Array.isArray(res.data?.plans) && res.data.plans.length > 0) {
                    setPlans(res.data.plans);
                }
            })
            .catch(() => {});
    }, []);

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="cv-auto relative overflow-hidden border-y border-slate-200/60 bg-white py-20 sm:py-28"
        >
            <AmbientGlow color="#2B5CAB" side="left" className="-left-40 top-16 h-[440px] w-[440px]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                {/* Intro */}
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <p
                            data-testid="pricing-eyebrow"
                            className="inline-flex items-center gap-2.5 rounded-full border border-brand-purple/15 bg-canvas px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-ink/60"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
                            Membership Plans
                        </p>
                        <h2 className="mt-6 text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                            Your Workspace. <span className="text-gradient">Your Plan.</span>
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="max-w-md text-sm font-medium leading-relaxed text-ink/55 md:text-base"
                    >
                        Choose the workspace that fits the way you work and grow.
                    </motion.p>
                </div>

                {/* Plans */}
                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-7">
                    {plans.map((plan, i) => {
                        const num = String(i + 1).padStart(2, "0");
                        return (
                        <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 26 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.65, ease: EASE, delay: i * 0.1 }}
                            whileHover={{ y: -5 }}
                            data-testid={`pricing-card-${plan.id}`}
                            className={`rounded-[1.9rem] transition-shadow duration-500 ${
                                plan.featured
                                    ? "bg-gradient-to-br from-brand-purple via-brand-blue to-brand-teal p-[1.5px] shadow-[0_36px_80px_-40px_rgba(43,92,171,0.5)] lg:-translate-y-3"
                                    : "border border-slate-200/80 hover:shadow-[0_28px_60px_-35px_rgba(22,50,79,0.35)]"
                            }`}
                        >
                            <div className="flex h-full flex-col rounded-[1.75rem] bg-white p-8">
                                <div className="flex items-baseline justify-between">
                                    <span
                                        className={`text-[13px] font-bold tracking-wider ${
                                            plan.featured ? "text-gradient" : "text-slate-300"
                                        }`}
                                    >
                                        {num}
                                    </span>
                                    {plan.featured && (
                                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" aria-hidden="true" />
                                    )}
                                </div>

                                <h3 className="mt-4 text-[22px] font-bold tracking-tight text-ink">
                                    {plan.name}
                                </h3>

                                <div className="mt-6">
                                    <p
                                        data-testid={`pricing-price-${plan.id}`}
                                        className="text-[44px] font-bold leading-none tracking-tight text-ink"
                                    >
                                        {plan.price}
                                    </p>
                                    <p className="mt-2 text-[12px] font-bold uppercase tracking-[0.18em] text-ink/40">
                                        {plan.unit}
                                    </p>
                                </div>

                                <div className="mt-6 border-t border-slate-100 pt-5">
                                    <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-ink/40">
                                        Suitable for
                                    </p>
                                    <p className="mt-1.5 text-sm font-medium leading-relaxed text-ink/60">
                                        {plan.suitable}
                                    </p>
                                </div>

                                <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
                                    {INCLUDES.map((inc) => (
                                        <span key={inc.label} className="flex items-center gap-1.5 text-[11px] font-semibold text-ink/45">
                                            <inc.icon size={12} strokeWidth={2.4} className="text-brand-blue/70" aria-hidden="true" />
                                            {inc.label}
                                        </span>
                                    ))}
                                </div>

                                <button
                                    data-testid={`pricing-enquire-${plan.id}`}
                                    onClick={() => scrollToId("#book-demo")}
                                    className={`group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-all duration-300 active:scale-[0.98] ${
                                        plan.featured
                                            ? "btn-gradient text-white"
                                            : "border border-slate-300/90 bg-white text-ink hover:-translate-y-0.5 hover:border-ink/30 hover:shadow-[0_12px_28px_-14px_rgba(22,50,79,0.35)]"
                                    }`}
                                >
                                    Enquire Now
                                    <ArrowRight size={15} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                            </div>
                        </motion.div>
                        );
                    })}
                </div>

                {/* Demo bridge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mt-16 flex flex-col items-center gap-4 text-center"
                >
                    <p className="text-lg font-bold tracking-tight text-ink md:text-xl">
                        Not sure which plan is right for you?
                    </p>
                    <p className="max-w-md text-sm font-medium leading-relaxed text-ink/50">
                        Visit Work Island and experience the workspace before choosing your plan.
                    </p>
                    <button
                        data-testid="pricing-book-demo-btn"
                        onClick={() => scrollToId("#book-demo")}
                        className="btn-gradient group mt-2 inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-bold text-white"
                    >
                        Book a Free Demo
                        <ArrowRight size={17} strokeWidth={2.6} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
